import { useState, useEffect } from "react";
import { listTools, type Tool, type WorkflowNode } from "../api/client.js";

const NODE_COLORS: Record<string, { bg: string; text: string }> = {
  tool_call: { bg: "#eff6ff", text: "#1e40af" },
  llm_call: { bg: "#f0fdf4", text: "#166534" },
  conditional: { bg: "#fefce8", text: "#854d0e" },
  human_approval: { bg: "#fdf2f8", text: "#9d174d" },
  transform: { bg: "#f5f3ff", text: "#5b21b6" },
};

export function NodeConfigPanel({
  node,
  onUpdate,
  onDelete,
  onClose,
  toolPolicyProfile,
}: {
  node: WorkflowNode;
  onUpdate: (nodeId: string, updates: Partial<WorkflowNode>) => void;
  onDelete: (nodeId: string) => void;
  onClose: () => void;
  toolPolicyProfile?: string;
}) {
  const colors = NODE_COLORS[node.type] ?? { bg: "#f8fafc", text: "#334155" };

  return (
    <div className="flex w-72 shrink-0 flex-col border-l border-ink-100 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-ink-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <span
            className="rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase"
            style={{ backgroundColor: colors.bg, color: colors.text }}
          >
            {node.type.replace("_", " ")}
          </span>
        </div>
        <button onClick={onClose} className="text-ink-400 hover:text-ink-600">
          <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Label */}
        <Field label="Label">
          <input
            value={node.label ?? ""}
            onChange={(e) => onUpdate(node.id, { label: e.target.value })}
            className="input text-sm"
            placeholder="Node label"
          />
        </Field>

        {/* ID (read-only) */}
        <Field label="Node ID">
          <input value={node.id} readOnly className="input text-sm bg-surface-1 text-ink-500 font-mono" />
        </Field>

        {/* Type-specific config */}
        {node.type === "tool_call" && (
          <ToolCallConfig node={node} onUpdate={onUpdate} toolPolicyProfile={toolPolicyProfile} />
        )}
        {node.type === "llm_call" && <LLMCallConfig node={node} onUpdate={onUpdate} />}
        {node.type === "conditional" && <ConditionalConfig node={node} onUpdate={onUpdate} />}
        {node.type === "human_approval" && <HumanApprovalConfig node={node} onUpdate={onUpdate} />}
        {node.type === "transform" && <TransformConfig node={node} onUpdate={onUpdate} />}

        {/* Retry policy */}
        <RetryPolicyConfig node={node} onUpdate={onUpdate} />
      </div>

      {/* Footer */}
      <div className="border-t border-ink-100 px-4 py-3">
        <button
          onClick={() => onDelete(node.id)}
          className="btn-secondary w-full text-xs text-danger hover:bg-red-50"
        >
          Delete Node
        </button>
      </div>
    </div>
  );
}

// ── Per-Type Config Forms ──────────────────────────────────────────────────────

function ToolCallConfig({
  node,
  onUpdate,
  toolPolicyProfile,
}: {
  node: WorkflowNode;
  onUpdate: (id: string, u: Partial<WorkflowNode>) => void;
  toolPolicyProfile?: string;
}) {
  const [tools, setTools] = useState<Tool[]>([]);

  useEffect(() => {
    listTools()
      .then((res) => {
        let filtered = res.tools;
        // For read-only profiles, exclude high-risk tools
        if (toolPolicyProfile === "read-only") {
          filtered = filtered.filter((t) => t.riskLevel === "low" || t.riskLevel === "medium");
        }
        setTools(filtered);
      })
      .catch(() => {});
  }, [toolPolicyProfile]);

  const config = node.config as { toolName?: string; staticArgs?: Record<string, unknown> };

  return (
    <>
      <Field label="Tool">
        <select
          value={config.toolName ?? ""}
          onChange={(e) =>
            onUpdate(node.id, { config: { ...node.config, toolName: e.target.value } })
          }
          className="input text-sm"
        >
          <option value="">Select a tool...</option>
          {tools.map((t) => (
            <option key={t.name} value={t.name}>
              {t.name} ({t.riskLevel})
            </option>
          ))}
        </select>
      </Field>
      <Field label="Static Args (JSON)">
        <textarea
          value={JSON.stringify(config.staticArgs ?? {}, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              onUpdate(node.id, { config: { ...node.config, staticArgs: parsed } });
            } catch {
              // Don't update on invalid JSON
            }
          }}
          rows={3}
          className="input font-mono text-xs"
        />
      </Field>
    </>
  );
}

function LLMCallConfig({
  node,
  onUpdate,
}: {
  node: WorkflowNode;
  onUpdate: (id: string, u: Partial<WorkflowNode>) => void;
}) {
  const config = node.config as { prompt?: string; model?: string; maxTokens?: number; temperature?: number };
  const models = [
    "claude-sonnet-4-5-20250929",
    "claude-haiku-4-5-20251001",
    "gpt-4o",
    "gpt-4o-mini",
    "gemini-2.0-flash",
  ];

  return (
    <>
      <Field label="Prompt Template">
        <textarea
          value={config.prompt ?? ""}
          onChange={(e) =>
            onUpdate(node.id, { config: { ...node.config, prompt: e.target.value } })
          }
          rows={5}
          className="input text-sm"
          placeholder="Use {{variable}} for interpolation"
        />
        <p className="mt-1 text-2xs text-ink-400">
          Use {"{{variable}}"} syntax for dynamic inputs
        </p>
      </Field>
      <Field label="Model">
        <select
          value={config.model ?? ""}
          onChange={(e) =>
            onUpdate(node.id, { config: { ...node.config, model: e.target.value } })
          }
          className="input text-sm"
        >
          {models.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Max Tokens">
          <input
            type="number"
            value={config.maxTokens ?? ""}
            onChange={(e) =>
              onUpdate(node.id, {
                config: { ...node.config, maxTokens: e.target.value ? parseInt(e.target.value) : undefined },
              })
            }
            className="input text-sm"
            placeholder="4096"
          />
        </Field>
        <Field label="Temperature">
          <input
            type="number"
            step="0.1"
            min="0"
            max="2"
            value={config.temperature ?? ""}
            onChange={(e) =>
              onUpdate(node.id, {
                config: { ...node.config, temperature: e.target.value ? parseFloat(e.target.value) : undefined },
              })
            }
            className="input text-sm"
            placeholder="1.0"
          />
        </Field>
      </div>
    </>
  );
}

function ConditionalConfig({
  node,
  onUpdate,
}: {
  node: WorkflowNode;
  onUpdate: (id: string, u: Partial<WorkflowNode>) => void;
}) {
  const config = node.config as {
    expression?: string;
    branches?: Record<string, string>;
    defaultBranch?: string;
  };

  return (
    <>
      <Field label="JSONPath Expression">
        <input
          value={config.expression ?? ""}
          onChange={(e) =>
            onUpdate(node.id, { config: { ...node.config, expression: e.target.value } })
          }
          className="input font-mono text-sm"
          placeholder="$.result.status"
        />
      </Field>
      <Field label="Branches (JSON)">
        <textarea
          value={JSON.stringify(config.branches ?? {}, null, 2)}
          onChange={(e) => {
            try {
              const parsed = JSON.parse(e.target.value);
              onUpdate(node.id, { config: { ...node.config, branches: parsed } });
            } catch {
              // Don't update on invalid JSON
            }
          }}
          rows={3}
          className="input font-mono text-xs"
          placeholder='{"true": "yes", "false": "no"}'
        />
      </Field>
      <Field label="Default Branch">
        <input
          value={config.defaultBranch ?? ""}
          onChange={(e) =>
            onUpdate(node.id, { config: { ...node.config, defaultBranch: e.target.value || undefined } })
          }
          className="input text-sm"
          placeholder="fallback"
        />
      </Field>
    </>
  );
}

function HumanApprovalConfig({
  node,
  onUpdate,
}: {
  node: WorkflowNode;
  onUpdate: (id: string, u: Partial<WorkflowNode>) => void;
}) {
  const config = node.config as { prompt?: string; timeoutMs?: number };
  const timeoutMinutes = Math.round((config.timeoutMs ?? 86400000) / 60000);

  return (
    <>
      <Field label="Approval Message">
        <textarea
          value={config.prompt ?? ""}
          onChange={(e) =>
            onUpdate(node.id, { config: { ...node.config, prompt: e.target.value } })
          }
          rows={3}
          className="input text-sm"
          placeholder="Please approve this action."
        />
      </Field>
      <Field label="Timeout (minutes)">
        <input
          type="number"
          min="1"
          value={timeoutMinutes}
          onChange={(e) => {
            const mins = parseInt(e.target.value) || 1440;
            onUpdate(node.id, { config: { ...node.config, timeoutMs: mins * 60000 } });
          }}
          className="input text-sm"
        />
      </Field>
    </>
  );
}

function TransformConfig({
  node,
  onUpdate,
}: {
  node: WorkflowNode;
  onUpdate: (id: string, u: Partial<WorkflowNode>) => void;
}) {
  const config = node.config as { template?: string; pick?: string[]; omit?: string[] };
  const [mode, setMode] = useState<"pick" | "omit" | "template">(
    config.template ? "template" : config.omit?.length ? "omit" : "pick",
  );

  return (
    <>
      <Field label="Operation">
        <div className="flex gap-1">
          {(["pick", "omit", "template"] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`flex-1 rounded-md px-2 py-1 text-xs font-medium ${
                mode === m ? "bg-ink-900 text-white" : "bg-surface-1 text-ink-600"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </Field>
      {mode === "template" ? (
        <Field label="Template String">
          <textarea
            value={config.template ?? ""}
            onChange={(e) =>
              onUpdate(node.id, { config: { template: e.target.value } })
            }
            rows={3}
            className="input font-mono text-xs"
            placeholder="${input.name} processed"
          />
        </Field>
      ) : (
        <Field label={`Fields to ${mode}`}>
          <input
            value={(mode === "pick" ? config.pick : config.omit)?.join(", ") ?? ""}
            onChange={(e) => {
              const fields = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
              onUpdate(node.id, {
                config: mode === "pick" ? { pick: fields } : { omit: fields },
              });
            }}
            className="input text-sm"
            placeholder="field1, field2, field3"
          />
        </Field>
      )}
    </>
  );
}

// ── Retry Policy ────────────────────────────────────────────────────────────

function RetryPolicyConfig({
  node,
  onUpdate,
}: {
  node: WorkflowNode;
  onUpdate: (id: string, u: Partial<WorkflowNode>) => void;
}) {
  const retry = node.retryPolicy;
  const [enabled, setEnabled] = useState(!!retry);

  return (
    <div className="border-t border-ink-100 pt-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-ink-700">Retry Policy</span>
        <button
          onClick={() => {
            if (enabled) {
              onUpdate(node.id, { retryPolicy: undefined });
            } else {
              onUpdate(node.id, {
                retryPolicy: { maxAttempts: 3, strategy: "fixed", delayMs: 1000 },
              });
            }
            setEnabled(!enabled);
          }}
          className={`relative h-5 w-9 rounded-full transition-colors ${
            enabled ? "bg-primary" : "bg-ink-200"
          }`}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
              enabled ? "translate-x-4" : "translate-x-0.5"
            }`}
          />
        </button>
      </div>
      {enabled && retry && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Max Attempts">
              <input
                type="number"
                min="1"
                max="10"
                value={retry.maxAttempts}
                onChange={(e) =>
                  onUpdate(node.id, {
                    retryPolicy: { ...retry, maxAttempts: parseInt(e.target.value) || 1 },
                  })
                }
                className="input text-sm"
              />
            </Field>
            <Field label="Delay (ms)">
              <input
                type="number"
                min="100"
                max="60000"
                value={retry.delayMs}
                onChange={(e) =>
                  onUpdate(node.id, {
                    retryPolicy: { ...retry, delayMs: parseInt(e.target.value) || 1000 },
                  })
                }
                className="input text-sm"
              />
            </Field>
          </div>
          <Field label="Strategy">
            <select
              value={retry.strategy}
              onChange={(e) =>
                onUpdate(node.id, { retryPolicy: { ...retry, strategy: e.target.value } })
              }
              className="input text-sm"
            >
              <option value="fixed">Fixed</option>
              <option value="exponential">Exponential</option>
            </select>
          </Field>
        </div>
      )}
    </div>
  );
}

// ── Shared ─────────────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-2xs font-semibold uppercase tracking-wider text-ink-400">
        {label}
      </label>
      {children}
    </div>
  );
}
